import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, UploadSimple, X, Trash, DownloadSimple, FolderSimple } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialog";

const CATEGORIES = ["Bye-laws", "AGM Minutes", "Circulars", "Financials", "Forms & NOCs", "Other"];
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function Documents() {
  const { user } = useAuth();
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user?.role);
  const { confirm } = useDialog();
  const [docs, setDocs] = useState([]);
  const [up, setUp] = useState(false);

  const load = useCallback(() => { api.get("/api/documents").then(({ data }) => setDocs(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    docs.forEach((d) => { (g[d.category] = g[d.category] || []).push(d); });
    return CATEGORIES.filter((c) => g[c]).map((c) => [c, g[c]]);
  }, [docs]);

  async function del(d) {
    if (!(await confirm({ title: "Remove document?", message: d.title, confirmText: "Remove", danger: true }))) return;
    await api.delete(`/api/documents/${d.id}`); load();
  }

  return (
    <div className="space-y-5 font-sans" data-testid="documents-screen">
      <div className="flex items-end justify-between">
        <div><p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Society</p><h1 className="font-display text-3xl font-extrabold text-white">Documents</h1></div>
        {isStaff && <button onClick={() => setUp(true)} data-testid="doc-upload-btn" className="inline-flex items-center gap-2 rounded-2xl bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan"><UploadSimple size={16} weight="bold" /> Upload</button>}
      </div>

      {docs.length === 0 ? <div className="card p-8 text-center text-sm text-white/45">No documents yet.</div>
        : grouped.map(([cat, items]) => (
          <div key={cat}>
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55"><FolderSimple size={13} weight="fill" /> {cat}</p>
            <div className="space-y-2">
              {items.map((d) => (
                <div key={d.id} className="card flex items-center gap-3 p-3.5" data-testid={`doc-${d.id}`}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-neon-cyan/12 text-neon-cyan"><FileText size={20} weight="duotone" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-[14px] font-semibold text-white">{d.title}</p>
                    <p className="truncate text-[12px] text-white/55">{d.fileName} · {kb(d.size)} · {d.uploadedByName}</p>
                  </div>
                  {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="rounded-xl bg-white/5 p-2 text-white/70 hover:text-neon-cyan" title="Open"><DownloadSimple size={18} weight="bold" /></a>}
                  {isStaff && <button onClick={() => del(d)} className="rounded-xl p-2 text-white/40 hover:text-neon-pink"><Trash size={16} weight="bold" /></button>}
                </div>
              ))}
            </div>
          </div>
        ))}

      <AnimatePresence>{up && <UploadSheet onClose={() => setUp(false)} onDone={() => { setUp(false); load(); }} />}</AnimatePresence>
    </div>
  );
}

function UploadSheet({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef();
  const valid = title.trim().length >= 2 && file;
  async function submit() {
    if (!valid) { setErr("Add a title and choose a file."); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData(); fd.append("title", title.trim()); fd.append("category", category); fd.append("file", file);
      await api.post("/api/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail === "file_too_large" ? "File too large (max 15 MB)." : "Upload failed."); setBusy(false); }
  }
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="doc-sheet">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl font-bold text-white">Upload document</h3><button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button></div>
        <div className="mt-4 space-y-4">
          <div><label className="label">Title</label><input className="field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. AGM Minutes — June 2026" data-testid="doc-title" /></div>
          <div><label className="label">Category</label><select className="field" value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c} className="bg-ink-800">{c}</option>)}</select></div>
          <div>
            <label className="label">File (PDF, image, doc — max 15 MB)</label>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" />
            <button onClick={() => fileRef.current?.click()} className="flex w-full items-center gap-2 rounded-2xl border border-dashed border-white/20 bg-white/[0.02] px-4 py-3 text-left text-[13px] text-white/70">
              <UploadSimple size={16} weight="bold" /> {file ? file.name : "Choose a file"}
            </button>
          </div>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!valid || busy} onClick={submit} data-testid="doc-submit" className="btn-primary w-full disabled:opacity-50">{busy ? "Uploading…" : "Upload"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
